import { useState } from "react";
import { PROBLEM_CODES, api, envelopeOverrun, problemCode, useApiMutation } from "../../api";
import type { Timesheet } from "../../api";
import { Button, Card, ConfirmDialog, Stat, useToast } from "../../components";
import { formatTokens, subAmounts } from "../../format";
import { FeuilleStatut } from "./FeuilleStatut";
import { GrilleMois } from "./GrilleMois";
import { RefusFeuilleModal } from "./RefusFeuilleModal";
import { messageFeuille } from "./messages";
import { aujourdhuiLocal, depuisEntries, formatJours, libelleMois, parseMois } from "./mois";
import styles from "./Feuilles.module.css";

export type FeuilleFicheProps = {
  feuille: Timesheet;
  /** La feuille a changé (approuvée, refusée) : le parent la remplace. */
  onDecision: (maj: Timesheet) => void;
  /** Où mènent les missions de l'entreprise, pour étendre une enveloppe. */
  lienMissions: string | null;
};

/**
 * Une feuille vue par un valideur : le client ou l'ESN.
 *
 * Elle montre les jours et ce qu'ils valent — c'est la seule vue qui parle
 * d'argent — et porte les deux décisions. L'approbation est confirmée avec
 * son chiffre : c'est un débit sur l'enveloppe de la mission, pas un clic
 * anodin. Un dépassement d'enveloppe est expliqué avec ses deux montants et
 * la porte de sortie : étendre la mission ou refuser la feuille.
 */
export function FeuilleFiche({ feuille, onDecision, lienMissions }: FeuilleFicheProps) {
  const { notify } = useToast();
  const approbation = useApiMutation(api.approveTimesheet);
  const refus = useApiMutation(api.rejectTimesheet);
  const [confirmer, setConfirmer] = useState(false);
  const [refuser, setRefuser] = useState(false);

  const mois = parseMois(feuille.period_month);
  const estimation = feuille.estimated_tokens;
  const soumise = feuille.status === "submitted";

  async function approuver() {
    const maj = await approbation.run(feuille.id);
    if (maj === null) {
      return;
    }
    setConfirmer(false);
    notify({
      tone: "success",
      title: "Feuille approuvée",
      description:
        maj.approved_tokens !== undefined
          ? `${formatTokens(maj.approved_tokens)} tokens imputés sur « ${maj.mission_label} ».`
          : `La feuille de ${maj.consultant_email} est approuvée.`,
    });
    onDecision(maj);
  }

  async function refuserAvec(motif: string) {
    const maj = await refus.run(feuille.id, { reason: motif });
    if (maj === null) {
      return;
    }
    setRefuser(false);
    notify({ tone: "neutral", title: "Feuille refusée", description: "Le consultant est prévenu et peut corriger." });
    onDecision(maj);
  }

  const depassement = envelopeOverrun(approbation.error);

  return (
    <div className={styles.fiche}>
      <FeuilleStatut feuille={feuille} />

      <div className={styles.chiffres}>
        <Stat label="Jours déclarés" value={formatJours(feuille.total_half_days)} hint="Demi-journées comprises." />
        {estimation !== undefined && (
          <Stat
            label={feuille.status === "approved" ? "Imputé" : "Équivalent"}
            value={<span className="lde-tabular">{formatTokens(feuille.approved_tokens ?? estimation)}</span>}
            unit="tokens"
            bucket={feuille.status === "approved" ? "consumed" : "reserved"}
            hint={
              feuille.status === "approved"
                ? "Passé du réservé au consommé sur la mission."
                : "Au tarif de l'expertise de la mission ; c'est ce que l'approbation imputera."
            }
          />
        )}
      </div>

      <Card title={mois === null ? feuille.period_month : libelleMois(mois)} subtitle={`${feuille.mission_label} · ${feuille.consultant_email}`}>
        {mois !== null && <GrilleMois mois={mois} valeurs={depuisEntries(feuille.entries)} aujourdhui={aujourdhuiLocal()} />}
        <Legende />
      </Card>

      {depassement !== null && (
        <div className={styles.refus} role="alert">
          <p>
            L'enveloppe de la mission ne suffit pas : il reste{" "}
            <strong>{formatTokens(depassement.remaining)}</strong> tokens et la feuille en vaut{" "}
            <strong>{formatTokens(depassement.requested)}</strong>, soit{" "}
            <strong>{formatTokens(subAmounts(depassement.requested, depassement.remaining))}</strong> de trop.
            Rien n'a été imputé, la feuille reste en attente.
          </p>
          <p>
            Deux issues : étendre l'enveloppe par une nouvelle commande sur cette mission
            {lienMissions !== null ? " (voir les missions)" : ""}, ou refuser la feuille avec un motif.
          </p>
        </div>
      )}
      {approbation.error !== null && depassement === null && (
        <div className={styles.refus} role="alert">
          {messageFeuille(approbation.error)}
          {problemCode(approbation.error) === PROBLEM_CODES.timesheetNotSubmitted && " Rechargez la page pour voir son état."}
        </div>
      )}

      {soumise && (
        <div className={styles.actions}>
          <Button variant="primary" onClick={() => setConfirmer(true)} loading={approbation.pending}>
            Approuver
          </Button>
          <Button variant="secondary" onClick={() => setRefuser(true)} disabled={approbation.pending}>
            Refuser
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmer}
        title="Approuver cette feuille ?"
        description={
          estimation !== undefined
            ? `${formatJours(feuille.total_half_days)} seront imputés sur « ${feuille.mission_label} », soit ${formatTokens(estimation)} tokens passés du réservé au consommé. Ce geste ne se défait pas.`
            : `${formatJours(feuille.total_half_days)} seront imputés sur « ${feuille.mission_label} ». Ce geste ne se défait pas.`
        }
        confirmLabel="Approuver et imputer"
        pending={approbation.pending}
        onConfirm={() => void approuver()}
        onCancel={() => setConfirmer(false)}
      />

      <RefusFeuilleModal
        open={refuser}
        pending={refus.pending}
        erreur={refus.error !== null ? messageFeuille(refus.error) : null}
        onConfirm={(motif) => void refuserAvec(motif)}
        onCancel={() => {
          setRefuser(false);
          refus.reset();
        }}
      />
    </div>
  );
}

/** La légende de la grille : ce que veulent dire les deux marques. */
export function Legende() {
  return (
    <p className={styles.legendeGrille}>
      <span className={styles.legendeItem}>
        <span className={`${styles.pastille} ${styles.pastilleDemi}`} aria-hidden="true" /> demi-journée
      </span>
      <span className={styles.legendeItem}>
        <span className={`${styles.pastille} ${styles.pastillePleine}`} aria-hidden="true" /> journée
      </span>
    </p>
  );
}
